import React from 'react';
import { OnboardingData } from '@/pages/onboarding/OnboardingWizard';

export interface OnboardingStepProps {
  data: OnboardingData;
  onNext: (stepData: Partial<OnboardingData>) => void;
  onPrevious: () => void;
  onComplete?: () => void;
  onFinish?: () => void;
  isLoading?: boolean;
  error?: string | null;
}

export interface OnboardingStepComponentProps extends OnboardingStepProps {
  // Additional props can be added by specific step components
}

export const OnboardingStep: React.FC<{
  children: React.ReactNode;
  title: string;
  description: string;
  onNext: (stepData: Partial<OnboardingData>) => void;
  onPrevious: () => void;
  onComplete?: () => void;
  onFinish?: () => void;
  isLoading?: boolean;
  canGoNext?: boolean;
  canGoPrevious?: boolean;
  showNext?: boolean;
  showPrevious?: boolean;
  showComplete?: boolean;
  showFinish?: boolean;
}> = ({
  children,
  title,
  description,
  onNext,
  onPrevious,
  onComplete,
  onFinish,
  isLoading = false,
  canGoNext = true,
  canGoPrevious = true,
  showNext = true,
  showPrevious = true,
  showComplete = false,
  showFinish = false,
}) => {
  const handleNext = () => {
    if (canGoNext && !isLoading) {
      onNext({});
    }
  };

  const handlePrevious = () => {
    if (canGoPrevious && !isLoading) {
      onPrevious();
    }
  };

  const handleComplete = () => {
    if (onComplete && !isLoading) {
      onComplete();
    }
  };

  const handleFinish = () => {
    if (onFinish && !isLoading) {
      onFinish();
    }
  };

  return (
    <div className="space-y-6">
      {/* Step Header */}
      <div className="text-center">
        <h2 className="text-2xl font-bold text-gray-900">{title}</h2>
        <p className="mt-2 text-gray-600">{description}</p>
      </div>

      {/* Step Content */}
      <div className="space-y-6">
        {children}
      </div>

      {/* Step Actions */}
      <div className="flex justify-between pt-6 border-t border-gray-200">
        <div>
          {showPrevious && (
            <button
              type="button"
              onClick={handlePrevious}
              disabled={!canGoPrevious || isLoading}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Previous
            </button>
          )}
        </div>

        <div className="flex space-x-3">
          {showNext && (
            <button
              type="button"
              onClick={handleNext}
              disabled={!canGoNext || isLoading}
              className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md shadow-sm hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Next
            </button>
          )}

          {showComplete && (
            <button
              type="button"
              onClick={handleComplete}
              disabled={isLoading}
              className="px-4 py-2 text-sm font-medium text-white bg-green-600 border border-transparent rounded-md shadow-sm hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isLoading ? 'Setting up...' : 'Complete Setup'}
            </button>
          )}

          {showFinish && (
            <button
              type="button"
              onClick={handleFinish}
              disabled={isLoading}
              className="px-4 py-2 text-sm font-medium text-white bg-green-600 border border-transparent rounded-md shadow-sm hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Finish
            </button>
          )}
        </div>
      </div>
    </div>
  );
};
